/**
 * The SPA's runtime configuration: `GET /config.json`, read once at start-up.
 *
 * ONE BUILD, TWO HOSTS (PLAN §10.2). The same `dist/` is served by CloudFront
 * and by an on-prem node's state service, so where the API lives cannot be a
 * build-time constant. Whoever serves the SPA serves this file beside it:
 *
 *     { "backend": { "kind": "cloud", "label": "dev",
 *                    "apiUrl": "https://api.dev.acp.mimir.tv", "wsUrl": "wss://ws.dev.acp.mimir.tv" },
 *       "auth":    { "kind": "cognito", "region": "eu-north-1",
 *                    "userPoolId": "eu-north-1_KxeJhVcSy", "clientId": "201ff90t90cfcnvhpp0slm4lrv" } }
 *
 *     { "backend": { "kind": "node", "label": "Studio A main", "apiUrl": "", "wsUrl": "/ws" },
 *       "auth":    { "kind": "dev" } }
 *
 * `apiUrl`/`wsUrl` may be relative to the page's origin (`""` is the origin
 * itself, `/ws` becomes `ws(s)://<host>/ws`) — the node case, and the Vite dev
 * server's proxy. `auth.kind: "dev"` is a node started with `ACP_DEV_AUTH`:
 * sign-in is `POST /dev/token` (no Cognito), used by `pnpm dev` and the
 * e2e-local suite. The backend is the *primary* one (§10.2); the connectivity
 * panel that watches the others is Wave 2b.
 *
 * Untrusted input (CLAUDE.md, branch 2): validated at the boundary with the
 * contract's schema (`@acp/api-contract/runtimeConfig.js`), which the two
 * producers — CloudFront's `infrastructure/web.tf` and the node's
 * `apps/state-service/src/web.ts` — write against too. Tolerantly (D37): the
 * host is deployed apart from this build (web.tf by `apply`, the SPA by
 * `web-deploy`; a tab or the service worker's shell outlives both), so a field
 * the host added is dropped, never a refusal to start.
 */
import { parseContract } from '@acp/api-contract/parsing.js';
import { type RuntimeConfigWire, runtimeConfigWireSchema } from '@acp/api-contract/runtimeConfig.js';
import { z } from 'zod';

/**
 * The config as the app runs on it: the backend is the cloud or a node. The console host's `kind: 'console'` (D39)
 * is the cloud's API with the staff console's shell instead of the customer app — so it becomes `kind: 'cloud'` here
 * plus `shell: 'console'` (`ConfigResult`), and nothing below the shell needs to know a third kind.
 */
export type AppConfig = Omit<RuntimeConfigWire, 'backend'> & { readonly backend: Omit<RuntimeConfigWire['backend'], 'kind'> & { readonly kind: 'cloud' | 'node' } };

/** Which app this host serves (D39): the customer app, or the staff console. */
export type Shell = 'app' | 'console';

export type ConfigResult = { readonly ok: true; readonly config: AppConfig; readonly shell: Shell } | { readonly ok: false; readonly error: string };

export function parseRuntimeConfig(value: unknown): ConfigResult {
  const result = parseContract(runtimeConfigWireSchema, value, 'tolerant');
  if (!result.success) return { ok: false, error: z.prettifyError(result.error) };
  const { backend, ...others } = result.data;
  // A kind a newer host writes (the enum is open): this build cannot know which app that host wants, so it says so.
  if (backend.kind === 'unknown') return { ok: false, error: 'this host serves a kind of ACP app this version does not know: reload the page to get the current one' };
  const kind = backend.kind === 'console' ? 'cloud' : backend.kind;
  return { ok: true, config: { ...others, backend: { ...backend, kind } }, shell: backend.kind === 'console' ? 'console' : 'app' };
}

/** Absolute endpoints: a relative `apiUrl`/`wsUrl` resolved against the page's origin, no trailing slash. */
export function resolveEndpoints(backend: AppConfig['backend'], origin: string): { readonly apiUrl: string; readonly wsUrl: string } {
  const api = new URL(backend.apiUrl === '' ? '/' : backend.apiUrl, origin);
  const ws = new URL(backend.wsUrl, origin);
  if (ws.protocol === 'http:') ws.protocol = 'ws:';
  if (ws.protocol === 'https:') ws.protocol = 'wss:';
  return { apiUrl: stripTrailingSlash(api.href), wsUrl: stripTrailingSlash(ws.href) };
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export type LoadConfig = (fetchJson: (path: string) => Promise<{ status: number; json: () => Promise<unknown> }>) => Promise<ConfigResult>;

/** Fetch and validate `/config.json`. Never throws: a missing or malformed file is a result the start page explains. */
export const loadRuntimeConfig: LoadConfig = async (fetchJson) => {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await fetchJson('/config.json');
  } catch (err) {
    return { ok: false, error: `could not fetch /config.json: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (response.status !== 200) return { ok: false, error: `/config.json answered HTTP ${String(response.status)}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { ok: false, error: '/config.json is not JSON' };
  }
  return parseRuntimeConfig(body);
};
